import { SubmitHandler, useForm } from 'react-hook-form';
import { useState } from 'react';
import { Button } from '@/components/button';
import { Checkbox } from '../inputs/checkbox';
import { Form } from '@/components/form';
import { StateSelector } from '@/components/form-controls/state-selector';
import { Select } from '../inputs/select';
import { TextInput } from '../inputs/text-input';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/contexts/i18n-context';
import { FormValues, SurrogateSchema } from '@/pages/surrogate/create-profile-surrogate-page';
import styles from './surrogate-form.module.css';
import { GetPersonalInfoResponse, PatientRelationships } from '@/api/api.schemas';
import { PhoneInput } from '../inputs/phone-input';
import { PreferredTitleSelector } from '@/components/form-controls/preferred-title-selector';
import { Section } from '@/components/containers/section';
import { FormButtonRow } from '@/components/containers/form-button-row';
import { formatPhoneNumber } from '@/util/helpers';
import { DemographicsConfig } from '@/pages/profile-creation/demographics-config';
import { useBranding } from '@/outlets/branding-outlet';
import { IdentityVerificationResetModal } from '@/components/identity-verification/identity-verification-reset-modal';
import { hasIdentityFieldsChanged } from '@/util/identity-fields';

interface SurrogateFormProps {
  defaultValues?: Partial<FormValues>;
  onSubmit: (data: FormValues) => void;
  personalInfo?: GetPersonalInfoResponse;
  config: DemographicsConfig;
  isLoading: boolean;
  surrogateVerified: boolean;
}

export function SurrogateForm({
  defaultValues,
  onSubmit,
  personalInfo,
  config,
  isLoading,
  surrogateVerified,
}: SurrogateFormProps) {
  const { t } = useTranslation();
  const { hasIdVerification } = useBranding();
  const [pendingFormData, setPendingFormData] = useState<FormValues>();

  const originalIdentityValues = {
    firstName: defaultValues?.firstName ?? '',
    lastName: defaultValues?.lastName ?? '',
    birthday: defaultValues?.birthday ?? '',
  };

  const showIdentityVerificationWarnings = hasIdVerification && surrogateVerified;

  const form = useForm<FormValues>({
    resolver: zodResolver(SurrogateSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    defaultValues,
  });

  const handleCopyClick = () => {
    form.setValue('email', personalInfo?.email ?? '');
    form.setValue('phone', formatPhoneNumber(personalInfo?.cellPhone));
    form.setValue('address1', personalInfo?.mailingAddress1 ?? '');
    form.setValue('address2', personalInfo?.mailingAddress2 ?? '');
    form.setValue('city', personalInfo?.city ?? '');
    form.setValue('state', personalInfo?.state ?? '');
    form.setValue('zip', personalInfo?.zip ?? '');
  };

  const textMe = form.watch('textMe');
  const textPatient = form.watch('textPatient');

  const handleSubmit: SubmitHandler<FormValues> = (data) => {
    if (
      showIdentityVerificationWarnings &&
      hasIdentityFieldsChanged(originalIdentityValues, data)
    ) {
      setPendingFormData(data);
      return;
    }

    onSubmit(data);
  };

  const handleConfirmIdentityReset = () => {
    if (!pendingFormData) {
      return;
    }

    onSubmit(pendingFormData);
    setPendingFormData(undefined);
  };

  return (
    <Form onSubmit={handleSubmit} form={form}>
      <Section>
        <h2 className="h4">{t('surrogate.personalInfo.title')}</h2>
        <div className={styles.formInputs}>
          <Select
            name="relationshipToPatient"
            className={styles.fullRow}
            placeholder={t('surrogate.relationshipToPatient.placeholder')}
            label={t('surrogate.relationshipToPatient.label')}
            options={Object.values(PatientRelationships).map((value) => ({
              value,
              label: t(`surrogate.relationshipToPatient.options.${value}`),
            }))}
          />
          <TextInput
            name="firstName"
            label={t('surrogate.surrogateName.firstName.label')}
            placeholder={t('surrogate.surrogateName.firstName.placeholder')}
            warningOnChange={
              showIdentityVerificationWarnings
                ? {
                    message: t('identityVerificationWarnings.field.name'),
                    defaultValue: originalIdentityValues.firstName,
                  }
                : undefined
            }
          />
          <TextInput
            name="lastName"
            label={t('surrogate.surrogateName.lastName.label')}
            placeholder={t('surrogate.surrogateName.lastName.placeholder')}
            warningOnChange={
              showIdentityVerificationWarnings
                ? {
                    message: t('identityVerificationWarnings.field.name'),
                    defaultValue: originalIdentityValues.lastName,
                  }
                : undefined
            }
          />
          <PreferredTitleSelector
            name="preferredTitle"
            label={t('surrogate.preferredTitle.label')}
            placeholder={t('surrogate.preferredTitle.placeholder')}
          />

          <TextInput
            name="birthday"
            label={t('surrogate.birthday.label')}
            description={t('surrogate.birthday.description')}
            type="date"
            withIconsIndicators={false}
            warningOnChange={
              showIdentityVerificationWarnings
                ? {
                    message: t('identityVerificationWarnings.field.dateOfBirth'),
                    defaultValue: originalIdentityValues.birthday,
                  }
                : undefined
            }
          />
        </div>
      </Section>

      <Section>
        <h2 className="h4">{t('surrogate.contactInfo.title')}</h2>
        <p className={styles.sectionDescription}>{t('surrogate.contactInfo.description')}</p>

        <Button variant="link" leftIcon="content_copy" onClick={handleCopyClick}>
          {t('surrogate.copyText')}
        </Button>

        <div className={styles.formInputs}>
          <TextInput
            name="email"
            label={t('surrogate.email.label')}
            placeholder={t('surrogate.email.placeholder')}
          />

          <PhoneInput name="phone" label={t('surrogate.phone.label')} />

          <TextInput
            name="address1"
            label={t('surrogate.surrogateMailingAddress.address1.label')}
            className={styles.fullRow}
            placeholder={t('surrogate.surrogateMailingAddress.address1.placeholder')}
            description={t('surrogate.surrogateMailingAddress.address1.description')}
          />
          <TextInput
            name="address2"
            label={t('surrogate.surrogateMailingAddress.address2.label')}
            className={styles.fullRow}
            placeholder={t('surrogate.surrogateMailingAddress.address2.placeholder')}
            description={t('surrogate.surrogateMailingAddress.address2.description')}
          />

          <TextInput
            name="city"
            label={t('surrogate.surrogateMailingAddress.city.label')}
            placeholder={t('surrogate.surrogateMailingAddress.city.placeholder')}
          />
          <StateSelector
            name="state"
            placeholder={t('surrogate.surrogateMailingAddress.state.placeholder')}
            label={t('surrogate.surrogateMailingAddress.state.label')}
          />
          <TextInput
            name="zip"
            label={t('surrogate.surrogateMailingAddress.zip.label')}
            placeholder={t('surrogate.surrogateMailingAddress.zip.placeholder')}
          />
        </div>
      </Section>

      {config.showTextMeSection && (
        <div className={styles.consentSection}>
          <h1 className={styles.title}>{t('surrogate.textSection.title')}</h1>
          <Checkbox name="textPatient" label={t('surrogate.textSection.label')} />
          <Checkbox name="textMe" label={t('surrogate.textSection.labelSurrogate')} />
          {(textMe || textPatient) && <i>{t('surrogate.textSection.description')}</i>}
        </div>
      )}

      <FormButtonRow
        buttonTextKey={config.submitButton.textKey}
        loading={isLoading}
        type="submit"
        secondaryButton={config.secondaryButton}
      />
      <IdentityVerificationResetModal
        isOpen={!!pendingFormData}
        onClose={() => setPendingFormData(undefined)}
        onConfirm={handleConfirmIdentityReset}
        isLoading={isLoading}
      />
    </Form>
  );
}
